import { computed, onScopeDispose, ref, toValue, type MaybeRefOrGetter } from 'vue'
import { storeToRefs } from 'pinia'
import { putLibraryGoal } from '@/library/api'
import type { LibraryGoal } from '@/library/library_types'
import { PL_SUMMARY_SAVING_STATES } from '@/presentlevels/presentlevels_constants'
import type { SavingState } from '@/presentlevels/presentlevels_types'
import type { AnalyticsTracker } from '@/tracking/analytics/analyticsTracker'
import { CONTENT_TYPES } from '@/util/access_visibility'
import { useAnchorPageStore } from '../anchor_page.store'
import { ANCHOR_PAGE_EVENT_NAMES } from '../anchor_page_event_names'

const JUST_SAVED_DISPLAY_MS = 3000
export const DISCARD_CONFIRMATION = 'Are you sure you want to discard any unsaved changes?'

/**
 * The saved anchor page's edit/save cycle: what the save button says, saving the copy's edited
 * text back to the library, and discarding it. `editVersion` changes whenever the editors must
 * start again from the saved text, so the page can key them on it.
 */
export function useSavedAnchorEditing(
  savedPageUuid: MaybeRefOrGetter<string>,
  currentLoad: () => number,
  analytics: AnalyticsTracker,
  onSaved: (savedCopy: LibraryGoal) => void
) {
  const anchorPageStore = useAnchorPageStore()
  const { hasEditedText } = storeToRefs(anchorPageStore)

  const isSaving = ref(false)
  const isJustSaved = ref(false)
  const editVersion = ref(0)
  let justSavedTimer: ReturnType<typeof setTimeout> | undefined

  const saveStatus = computed<SavingState>(() => {
    if (isSaving.value) return PL_SUMMARY_SAVING_STATES.SAVING
    if (isJustSaved.value) return PL_SUMMARY_SAVING_STATES.JUST_SAVED
    if (hasEditedText.value) return PL_SUMMARY_SAVING_STATES.UNSAVED_CHANGES
    return PL_SUMMARY_SAVING_STATES.NO_CHANGES
  })

  function showJustSaved() {
    isJustSaved.value = true
    clearTimeout(justSavedTimer)
    justSavedTimer = setTimeout(() => { isJustSaved.value = false }, JUST_SAVED_DISPLAY_MS)
  }

  async function save() {
    const uuid = toValue(savedPageUuid)
    const load = currentLoad()
    isSaving.value = true
    isJustSaved.value = false
    analytics.sendEvent(ANCHOR_PAGE_EVENT_NAMES.CLICKED_SAVE_CHANGES, { uuid, type: CONTENT_TYPES.SAVED_SUPER_PAGE })
    try {
      const savedCopy = await putLibraryGoal(uuid, anchorPageStore.savedAnchorUpdatePayload)
      // The page may have moved on to another copy, or reloaded this one, while the save was in flight;
      // its answer is not what the page now shows.
      const isStillShown = uuid === toValue(savedPageUuid) && load === currentLoad() && anchorPageStore.showSavedText(savedCopy)
      if (!isStillShown) return
      onSaved(savedCopy)
      editVersion.value++
      showJustSaved()
    } catch (error) {
      console.error('Error saving saved anchor page', error)
    } finally {
      isSaving.value = false
    }
  }

  function isLeavingUnsavedEditsConfirmed(): boolean {
    return !hasEditedText.value || window.confirm(DISCARD_CONFIRMATION)
  }

  function discard() {
    if (!window.confirm(DISCARD_CONFIRMATION)) return
    anchorPageStore.discardEdits()
    editVersion.value++
  }

  onScopeDispose(() => clearTimeout(justSavedTimer))

  return { saveStatus, editVersion, save, discard, isLeavingUnsavedEditsConfirmed }
}
